import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Typography } from "@mui/material";

import { IPD_COLORS, IPD_SHAPE, IpdButton, IpdConfirmDialog } from "../../ipd/utils";
import { getChargeCheck, postPendingCharges, priceDose, waiveDose } from "../api/emergencyBillingApi";
import { chargeCheckCount } from "../utils/charges";
import MedicinePriceDialog from "./MedicinePriceDialog";

const KIND_LABEL = {
  registration: "Registration",
  consult: "Consultation",
  specialist: "Specialist",
  bed: "Bed",
  procedure: "Procedure",
  equipment: "Equipment",
  order: "Test / scan",
  dose: "Medicine",
};

/**
 * What on this visit has not reached billing yet — the charge check.
 *
 * Charges are recorded as things happen and posted without anyone waiting, so
 * this lists only what went wrong: a charge billing refused (with why, and a
 * Retry once it is fixed), and a dose given with no stocked item chosen (a
 * give-now order's dose, or one logged without an item), which cannot be
 * priced until someone says what was given. It stays usable after the visit
 * closes: getting charges to billing is not a clinical edit.
 */
export default function ChargeCheckSection({ visit, refreshKey, onPosted }) {
  const [check, setCheck] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pricing, setPricing] = useState(null);
  // A dose being marked not chargeable, and why.
  const [waiving, setWaiving] = useState(null);
  const [waiveReason, setWaiveReason] = useState("");
  const [waiveBusy, setWaiveBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setCheck(await getChargeCheck(visit.id));
      setError("");
    } catch (e) {
      setError(e?.message || "Could not load the charge check");
    }
  }, [visit.id]);

  // Inside the Charges tab, read only once the tab has posted what was waiting
  // (refreshKey counts its loads); on its own, read straight away.
  useEffect(() => {
    if (refreshKey !== 0) load();
  }, [load, refreshKey]);

  const retry = async () => {
    setBusy(true);
    setError("");
    try {
      await postPendingCharges(visit.id);
      await load();
      await onPosted?.();
    } catch (e) {
      setError(e?.message || "Could not post the charges");
    } finally {
      setBusy(false);
    }
  };

  const choose = async (choice) => {
    try {
      await priceDose(visit.id, {
        administrationId: pricing.administration_id,
        itemId: choice.itemId,
        itemName: choice.itemName,
        batchNumber: choice.batchNumber,
        quantity: choice.quantity,
      });
      await retry();
    } catch (e) {
      setError(e?.message || "Could not price the dose");
    }
  };

  const waive = async () => {
    setWaiveBusy(true);
    try {
      await waiveDose(visit.id, { administrationId: waiving.administration_id, reason: waiveReason.trim() });
      setWaiving(null);
      setWaiveReason("");
      await load();
      await onPosted?.();
    } catch (e) {
      setWaiving(null);
      setError(e?.message || "Could not mark the dose not chargeable");
    } finally {
      setWaiveBusy(false);
    }
  };

  // Shown above the list, not instead of it, so Retry stays in reach.
  const alert = error && (
    <Alert severity="warning" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
      {error}
    </Alert>
  );
  if (!check) return alert || null;
  if (!chargeCheckCount(check)) {
    return (
      <>
        {alert}
        <Typography sx={{ mb: 2, fontSize: 13, fontWeight: 600, color: IPD_COLORS.success.text }}>
          All charges posted
        </Typography>
      </>
    );
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.75,
        border: `1px dashed ${IPD_COLORS.warning.text}`,
        borderRadius: `${IPD_SHAPE.radiusMd}px`,
      }}
    >
      {alert}
      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Charge check — not billed yet</Typography>

      {check.open.map((row) => (
        <Box key={row.id} sx={{ py: 0.5 }}>
          <Typography sx={{ fontSize: 13.5 }}>
            {KIND_LABEL[row.kind] || row.kind} · {row.service_name || "—"}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: row.status === "failed" ? IPD_COLORS.error.text : IPD_COLORS.textSecondary,
            }}
          >
            {row.last_error || "Waiting to be sent to billing"}
          </Typography>
        </Box>
      ))}

      {check.dosesToPrice.map((d) => (
        <Box
          key={`dose-${d.administration_id}`}
          role="group"
          aria-label={`Medicine ${d.medicine_name}`}
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, py: 0.5 }}
        >
          <Typography sx={{ fontSize: 13.5 }}>
            Medicine · {[d.medicine_name, d.dose, d.route].filter(Boolean).join(" ")} — no stocked item chosen
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <IpdButton size="small" variant="outlined" onClick={() => setPricing(d)}>
              Choose item
            </IpdButton>
            {check.canAdjust && (
              <IpdButton
                size="small"
                variant="outlined"
                onClick={() => {
                  setWaiveReason("");
                  setWaiving(d);
                }}
              >
                Not chargeable
              </IpdButton>
            )}
          </Box>
        </Box>
      ))}

      {check.open.length > 0 && (
        <IpdButton size="small" sx={{ mt: 1 }} onClick={retry} loading={busy}>
          Retry
        </IpdButton>
      )}

      <IpdConfirmDialog
        open={Boolean(waiving)}
        requireReason
        reasonLabel="Why is this dose not charged?"
        reasonValue={waiveReason}
        onReasonChange={setWaiveReason}
        title="Not chargeable?"
        description={
          waiving
            ? `${waiving.medicine_name} will not be billed — for the patient's own medicine, a sample, or similar. The reason is kept with the visit.`
            : ""
        }
        confirmLabel="Mark not chargeable"
        loading={waiveBusy}
        onConfirm={waive}
        onCancel={() => setWaiving(null)}
      />

      <MedicinePriceDialog
        open={Boolean(pricing)}
        visitId={visit.id}
        dose={pricing}
        onClose={() => setPricing(null)}
        onChoose={choose}
      />
    </Box>
  );
}
